import { useMemo, useRef, useState } from 'react';
import { RangeKeyDict } from 'react-date-range';

import {
  addDays,
  addYears,
  endOfDay,
  endOfToday,
  getTime,
  startOfDay,
  subYears,
} from 'date-fns';
import useSWR from 'swr';

import { STATUS_OPTIONS, TYPES_OPTIONS } from 'commons';
import {
  DateRangePicker,
  Header,
  Main,
  PageTitle,
  SearchInput,
  Select,
} from 'components';
import { useAuth } from 'hooks';
import { pixTransactionsService } from 'services';

import { PixTransactionsTable } from './components/pix-transactions-table';
import { TransactionsResume } from './components/transactions-resume';

const DEFAULT_PAST_SELECTION = -29;
const DEFAULT_START_DATE = () =>
  startOfDay(addDays(new Date(), DEFAULT_PAST_SELECTION));
const DEFAULT_END_DATE = () => endOfToday();

const PAST_YEARS_LIMIT = 10;
const FUTURE_YEARS_LIMIT = 1;

const MIN_DATE = subYears(new Date(), PAST_YEARS_LIMIT);
const MAX_DATE = addYears(new Date(), FUTURE_YEARS_LIMIT);

export function PixTransactionsReportPage() {
  const mainRef = useRef<HTMLDivElement>(null);
  const inputCodeRef = useRef<HTMLInputElement>(null);
  const { selectedStore } = useAuth();

  const [params, setParams] = useState({
    startDate: DEFAULT_START_DATE(),
    endDate: DEFAULT_END_DATE(),
    types: '',
    statuses: '',
    searchCode: '',
    pageSize: 10,
    currentPage: 0,
  });

  const { data: resumeData, isLoading: isResumeLoading } = useSWR(
    ['pix-transactions-resume', selectedStore.brand, selectedStore.name],
    pixTransactionsService.getResume
  );

  const apiParams = useMemo(
    () => ({
      startDate: getTime(params.startDate),
      endDate: getTime(params.endDate),
      types: params.types || undefined,
      statuses: params.statuses || undefined,
      code: params.searchCode || undefined,
      pageSize: params.pageSize.toString(),
      currentPage: params.currentPage.toString(),
    }),
    [params]
  );

  const {
    data: transactionsData,
    isLoading: isTransactionsLoading,
    mutate,
  } = useSWR(['pix-transactions', apiParams], () =>
    pixTransactionsService.getTransactions(apiParams)
  );

  const transactions = transactionsData?.results || [];
  const pagination = transactionsData?.pagination;

  const changeDateRange = (dateRange: RangeKeyDict) => {
    const { startDate, endDate } = dateRange.range1;
    if (!startDate || !endDate) return;

    setParams(prev => ({
      ...prev,
      startDate: startOfDay(startDate),
      endDate: endOfDay(endDate),
      currentPage: 0,
    }));
  };

  const submitDateRange = async () => {
    mutate();
  };

  const resetDateRangeFilter = () => {
    setParams(prev => ({
      ...prev,
      startDate: DEFAULT_START_DATE(),
      endDate: DEFAULT_END_DATE(),
    }));
  };

  const handleTypesChange = (e: any) => {
    const selectedTypes = e.target.value || '';
    setParams(prev => ({ ...prev, types: selectedTypes, currentPage: 0 }));
  };

  const handleStatusChange = (e: any) => {
    const selectedStatuses = e.target.value || '';
    setParams(prev => ({
      ...prev,
      statuses: selectedStatuses,
      currentPage: 0,
    }));
  };

  const handlePageChange = (page: number) => {
    setParams(prev => ({ ...prev, currentPage: page }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setParams(prev => ({
      ...prev,
      pageSize: newPageSize,
      currentPage: 0,
    }));
  };

  const clearSearchCode = () => {
    if (inputCodeRef.current) {
      inputCodeRef.current.value = '';
    }
    setParams(prev => ({ ...prev, searchCode: '', currentPage: 0 }));
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget as HTMLFormElement);
    const searchValue = (formData.get('search-code') as string) || '';
    setParams(prev => ({ ...prev, searchCode: searchValue, currentPage: 0 }));
  };

  const selectedTypesOptions = useMemo(() => {
    const typesArray = params.types.split(',').filter(Boolean);
    return TYPES_OPTIONS.filter(({ value }) => typesArray.includes(value));
  }, [params.types]);

  const selectedStatusesOptions = useMemo(() => {
    const statusesArray = params.statuses.split(',').filter(Boolean);
    return STATUS_OPTIONS.filter(({ value }) => statusesArray.includes(value));
  }, [params.statuses]);

  return (
    <Main>
      <Header />

      <div
        ref={mainRef}
        className="flex h-full flex-col gap-6 overflow-y-auto overflow-x-hidden
          px-4 pb-2 pt-8 md:px-12"
      >
        <PageTitle
          title="Relatório de transações Pix"
          subtitle="Acompanhe a evolução das transações financeiras de pedidos pagos via Pix."
        />

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 lg:flex-row">
            <TransactionsResume
              resumeData={resumeData}
              isLoading={isResumeLoading}
            />
          </div>

          <div
            className="flex max-w-[1400px] flex-col gap-2 xl:flex-row xl:gap-4"
          >
            <div className="w-full xl:w-auto">
              <DateRangePicker
                onChangeRange={changeDateRange}
                onSubmit={submitDateRange}
                onReset={resetDateRangeFilter}
                values={{
                  startDate: params.startDate,
                  endDate: params.endDate,
                }}
                mainRef={mainRef}
                maximumDate={MAX_DATE}
                minimumDate={MIN_DATE}
                options={{ scrollIntoView: true }}
              />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row xl:gap-4">
              <div className="w-full min-w-40 xl:w-auto xl:max-w-52">
                <Select.Wrapper
                  key="types"
                  name="types"
                  onChange={handleTypesChange}
                  options={TYPES_OPTIONS}
                  initialTagged={selectedTypesOptions}
                  multiple
                  searchable
                >
                  <Select.Input placeholder="Filtrar por Tipo" />
                  <Select.Tags maxTags={1} />
                  <Select.StatusArrow />
                </Select.Wrapper>
              </div>
              <div className="w-full min-w-40 xl:w-auto xl:max-w-52">
                <Select.Wrapper
                  key="statuses"
                  name="statuses"
                  onChange={handleStatusChange}
                  options={STATUS_OPTIONS}
                  initialTagged={selectedStatusesOptions}
                  multiple
                  searchable
                >
                  <Select.Input placeholder="Filtrar por Status" />
                  <Select.Tags maxTags={1} />
                  <Select.StatusArrow />
                </Select.Wrapper>
              </div>
            </div>
            <div className="flex w-full items-center xl:w-auto">
              <SearchInput
                label="N.º do pedido ou remessa"
                onSubmit={handleSearchSubmit}
                inputId="search-code"
                inputName="search-code"
                ref={inputCodeRef}
                value={params.searchCode}
                onClear={clearSearchCode}
              />
            </div>
          </div>

          <div
            className="flex flex-col items-center justify-center gap-2
              sm:flex-row xl:gap-4"
          >
            <PixTransactionsTable
              transactions={transactions}
              pagination={pagination}
              isLoading={isTransactionsLoading}
              pageSize={params.pageSize}
              onPageSizeChange={handlePageSizeChange}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </Main>
  );
}
